import React, { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import FinancingBanner from "@/components/FinancingBanner";
import { trackEvent } from "@/lib/analytics";
import { uns } from "@/lib/uns";
import Seo, { breadcrumbLd, faqLd } from "@/components/seo/Seo";

const SYNCHRONY_URL = "https://www.synchrony.com/mmc/CR244677105?sitecode=acewel403";

const benefits = [
  { icon: "⚡", title: "Quick application", desc: "Apply online in minutes with a short, simple form." },
  { icon: "🔧", title: "Use it for anything", desc: "Repairs, tires, batteries, maintenance, diagnostics — all eligible." },
  { icon: "⏱️", title: "Don't delay needed work", desc: "Address safety-critical repairs today instead of putting them off." },
  { icon: "💳", title: "Reusable credit line", desc: "Once approved, you can use it for future visits too." },
];

const steps = [
  { num: 1, icon: "📝", title: "Apply Online", desc: "Follow the Synchrony link, complete the short application from your phone or laptop." },
  { num: 2, icon: "✅", title: "Get a Decision", desc: "Receive your application decision quickly after submitting." },
  { num: 3, icon: "🔧", title: "Use It At The Shop", desc: "Bring your account info to Auto Basic Care and put it toward your service visit." },
];

const photos = [
  {
    ...uns("photo-1450101499163-c8848c66ca85", { w: 900, widths: [480, 720, 900] }),
    alt: "Customer and service advisor reviewing paperwork across a desk",
  },
  {
    ...uns("photo-1554224155-6726b3ff858f", { w: 900, widths: [480, 720, 900] }),
    alt: "Close-up of hands signing service authorization paperwork",
  },
  {
    ...uns("photo-1560179707-f14e90ef3623", { w: 900, widths: [480, 720, 900] }),
    alt: "Customer receiving their keys at the service counter",
  },
];

const faqs = [
  { q: "Can I use financing for any type of auto repair?", a: "Financing can generally be used for eligible auto services and parts at participating shops, including repairs, tires, batteries, and routine maintenance. Ask us at the shop what's eligible for your visit." },
  { q: "Do I need to apply before I come in?", a: "You can apply online any time — before, during, or after your visit. Applying beforehand can speed up your check-in." },
  { q: "Can I use it more than once?", a: "Once approved, your Synchrony account is a revolving line of credit, so you can use it for future services subject to your available credit." },
  { q: "Will Auto Basic Care see my personal financial information?", a: "No. The application is handled directly through Synchrony. We only see whether your account can be used to pay for your visit." },
  { q: "What if I have more questions?", a: "Give us a call at 205-405-1238 and we'll help walk you through your options." },
];

export default function Financing() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div className="abc-app">
      <Seo
        path="/financing"
        title="Auto Repair Financing in Birmingham AL — Synchrony"
        description="Flexible auto repair and tire financing at Auto Basic Care in Birmingham, AL. Apply online through Synchrony — 0% intro APR available, decisions in minutes."
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Financing", path: "/financing" },
          ]),
          faqLd(faqs),
        ]}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="financing-hero">
        <div className="container financing-hero-inner">
          <div className="badge">Flexible Financing · Powered by Synchrony</div>
          <h1>Get the repairs done now. Pay over time.</h1>
          <p className="financing-hero-p">
            Life doesn&apos;t wait for the perfect moment to need brakes, a battery, or new tires.
            With Synchrony financing, you can drive away today and pay for your service on a schedule that works for you.
          </p>
          <div className="button-row">
            <a
              href={SYNCHRONY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              data-testid="financing-hero-cta"
              onClick={() => trackEvent("Lead", { content_type: "financing", source: "hero-cta" })}
            >
              Apply for Financing →
            </a>
            <a href="tel:2054051238" className="btn btn-outline-light" data-testid="financing-hero-call">
              Call the Shop
            </a>
          </div>
        </div>
      </section>

      <main>
        {/* FEATURE ROW */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">Why Financing Helps</div>
                <h2 className="section-title">Move forward — don&apos;t put safety off.</h2>
              </div>
            </div>
            <div className="financing-feature">
              <div className="financing-feature-banner">
                <FinancingBanner variant="inline" />
              </div>
              <ul className="financing-benefits">
                {benefits.map((b) => (
                  <li key={b.title}>
                    <span className="financing-benefit-icon" aria-hidden="true">{b.icon}</span>
                    <div>
                      <strong>{b.title}</strong>
                      <p className="muted">{b.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="section" style={{ background: "var(--gray-soft)" }}>
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">How It Works</div>
                <h2 className="section-title">Three simple steps.</h2>
              </div>
            </div>
            <div className="process-grid">
              {steps.map((s) => (
                <div key={s.num} className="process-card">
                  <div className="num">{s.num}</div>
                  <div style={{ fontSize: 28, marginBottom: 8 }} aria-hidden="true">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PHOTO SECTION */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">Real Service. Real Care.</div>
                <h2 className="section-title">Walk in with questions, drive out with a plan.</h2>
              </div>
            </div>
            <div className="financing-photos">
              {photos.map((p) => (
                <figure key={p.src} className="financing-photo">
                  <img
                    src={p.src}
                    srcSet={p.srcSet}
                    sizes="(min-width: 900px) 33vw, 100vw"
                    alt={p.alt}
                    loading="lazy"
                    decoding="async"
                  />
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section" style={{ background: "var(--gray-soft)" }}>
          <div className="container" style={{ maxWidth: 880 }}>
            <div className="section-header">
              <div>
                <div className="eyebrow">Financing FAQ</div>
                <h2 className="section-title">Common questions.</h2>
              </div>
            </div>
            <div className="faq-list">
              {faqs.map((it, i) => {
                const isOpen = openIdx === i;
                return (
                  <div key={i} className={`faq-item ${isOpen ? "open" : ""}`}>
                    <button
                      className="faq-question"
                      onClick={() => setOpenIdx(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      data-testid={`financing-faq-${i}`}
                    >
                      <span>{it.q}</span>
                      <span className="plus" aria-hidden="true">{isOpen ? "−" : "+"}</span>
                    </button>
                    <div className="faq-answer">{it.a}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CLOSING CTA */}
        <section className="tires-cta">
          <div className="container">
            <h2>Ready to move forward?</h2>
            <p>
              Apply for Synchrony financing today, then bring your service info to Auto Basic Care —
              1700 31st Street SW, Birmingham, AL 35221. Questions? Call 205-405-1238.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <a
                href={SYNCHRONY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-dark"
                data-testid="financing-closing-cta"
                onClick={() => trackEvent("Lead", { content_type: "financing", source: "closing-cta" })}
              >
                Apply for Financing →
              </a>
              <a href="tel:2054051238" className="btn btn-outline-light" data-testid="financing-closing-call">
                Call 205-405-1238
              </a>
              <Link to="/#schedule" className="btn btn-outline-light" data-testid="financing-closing-schedule">
                Schedule Service
              </Link>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
